/** @jsxImportSource @emotion/react */
import { css, CSSObject } from "@emotion/react";

import { ReactNode } from "react";

import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import InfoIconWithTooltip from "../IconsWithTooltips/InfoIconWithTooltip/InfoIconWithTooltip";
import Separator from "../Separator/Separator";
import { TOGGLE_SWITCH_SIZES, ToggleSwitch } from "../ToggleSwitch";
import { useStyles } from "./styles";

interface ToggleItemWithTooltipProps {
  title: string;
  tooltipText?: string;
  onToggle: any;
  isChecked: boolean;
  isDisabled?: boolean;
  index?: number;
  totalItemsCount?: number;
  showSeparator?: boolean;
  testId?: string;
  readOnly?: boolean;
  customIconStyle?: {};
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
  customContainerStyle?: {};
  embeddedElement?: ReactNode;
  customSwitchStyle?: CSSObject;
}

const ToggleItemWithTooltip = ({
  title,
  tooltipText,
  onToggle,
  isChecked,
  isDisabled,
  index = 0,
  totalItemsCount = 1,
  showSeparator,
  testId,
  readOnly,
  customIconStyle,
  tooltipPlacement,
  customContainerStyle,
  embeddedElement,
  customSwitchStyle
}: ToggleItemWithTooltipProps) => {
  const {
    toggleItemContainer,
    toggleItemTitleBox,
    titleTypography,
    tooltipSpan
  } = useStyles(isDisabled, showSeparator);

  const displaySeparator = showSeparator ?? index < totalItemsCount - 1;

  return (
    <>
      <div css={css([toggleItemContainer, customContainerStyle])}>
        <div css={css(toggleItemTitleBox)}>
          <Typography
            variant="body2Medium"
            component="div"
            sx={titleTypography as CSSObject}
          >
            {title}
            {tooltipText && (
              <span css={css(tooltipSpan)}>
                <InfoIconWithTooltip
                  title={tooltipText}
                  iconTestId={`${title}-info icon`}
                  customIconStyle={customIconStyle}
                  placement={tooltipPlacement}
                />
              </span>
            )}
          </Typography>
        </div>
        <div
          css={css(
            { display: "flex", gap: "16px", marginLeft: "8px" },
            customSwitchStyle
          )}
        >
          {embeddedElement}
          <ToggleSwitch
            id={`${title}_toggle_switch`}
            size={TOGGLE_SWITCH_SIZES.Medium}
            checked={isChecked}
            disabled={isDisabled}
            onChange={onToggle}
            testId={testId ? `${testId}-toggle-switch` : undefined}
            readOnly={readOnly}
          />
        </div>
      </div>
      {displaySeparator && <Separator color={colors.gray200} />}
    </>
  );
};

export default ToggleItemWithTooltip;
